<template>
  <div class="container-fluid container-wrap col-sm-12 col-xs-12">
    <div style="margin-top: 28px"></div>
    <div class="card-header pb-0 col-sm-12 d-flex">
      <div class="col-sm-12 col-xs-12">
        <div class="col-sm-12 col-xs-12 justify-content-between titleContainer">
          <h1 class="fw-bold page-title">
            제품 관리
          </h1>
          <span class="pageDesc">구독 서비스의 제품을 등록하고 관리합니다.</span>
        </div>
      </div>
    </div>

    <!-- 제품 목록 테이블 -->
    <div class="card-body px-0 pt-0 pb-2 table-with-search" style="margin-top: 20px">
      <div class="card content-wrap-card col-sm-12 col-xs-12 tableCard p-0">
        <div class="table-buttons">
          <a-checkbox v-model:checked="includeInactive" @change="loadProductMasters">
            비활성 포함
          </a-checkbox>
          <a-input-search
            v-model:value="searchKeyword"
            placeholder="제품명으로 검색"
            style="width: 200px; margin-left: 16px;"
            @search="loadProductMasters"
          />
          <a-button type="primary" class="yellow-button add-product-btn" @click="handleAddProduct">
            <template #icon>
              <PlusOutlined />
            </template>
            제품 추가
          </a-button>
        </div>

        <div class="table-responsive table-wrap">
          <a-table
            class="ant-table-striped"
            size="small"
            bordered
            :loading="dataLoading"
            :columns="columns"
            :data-source="productList"
            :pagination="false"
            row-key="_id"
          >
            <template #emptyText>
              <div class="noData">
                <i class="fa-solid fa-exclamation-triangle" style="font-size: 24px; margin-bottom: 10px"/><br>
                <span>등록된 제품이 없습니다.</span>
              </div>
            </template>

            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'name'">
                <span class="product-name">{{ record.name }}</span>
              </template>
              <template v-else-if="column.key === 'isActive'">
                <a-tag :color="record.isActive ? 'green' : 'default'">
                  {{ record.isActive ? '활성' : '비활성' }}
                </a-tag>
              </template>
              <template v-else-if="column.key === 'actions'">
                <a-space>
                  <a-button size="small" type="primary" @click="handleEditProduct(record)">수정</a-button>
                  <a-button size="small" danger @click="handleDeleteProduct(record)">삭제</a-button>
                </a-space>
              </template>
            </template>
          </a-table>
        </div>
      </div>
    </div>

    <!-- 제품 추가/수정 모달 -->
    <a-modal
      v-model:open="modalVisible"
      :title="isEditMode ? '제품 수정' : '제품 추가'"
      :width="480"
      :destroy-on-close="true"
      :footer="null"
      @cancel="handleCancel"
    >
      <a-form
        ref="formRef"
        :model="formData"
        :rules="formRules"
        layout="vertical"
        class="modal-form"
      >
        <a-form-item label="제품명" name="name">
          <a-input v-model:value="formData.name" placeholder="제품명을 입력하세요" />
        </a-form-item>

        <a-form-item label="과금 방식" name="billingType">
          <a-radio-group v-model:value="formData.billingType">
            <a-radio
              v-for="bt in billingTypeOptions"
              :key="bt.value"
              :value="bt.value"
              :disabled="bt.disabled"
            >
              {{ bt.label }}
            </a-radio>
          </a-radio-group>
        </a-form-item>

        <a-form-item label="설치비(VAT포함)" name="installationFee">
          <a-input-number
            v-model:value="formData.installationFee"
            :formatter="value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
            :parser="value => value.replace(/[^\d]/g, '')"
            :controls="false"
            style="width: 100%"
            placeholder="설치비를 입력하세요"
            :min="0"
            :precision="0"
            @keypress="onlyNumber"
          />
        </a-form-item>

        <a-form-item :label="usageFeeLabel" name="price">
          <a-input-number
            v-model:value="formData.price"
            :formatter="value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
            :parser="value => value.replace(/[^\d]/g, '')"
            :controls="false"
            style="width: 100%"
            placeholder="사용료를 입력하세요"
            :min="0"
            :precision="0"
            @keypress="onlyNumber"
          />
        </a-form-item>

        <a-form-item label="설명" name="description">
          <a-textarea v-model:value="formData.description" placeholder="제품 설명을 입력하세요" :rows="3" />
        </a-form-item>
        <div class="modal-footer">
          <a-button @click="handleCancel">취소</a-button>
          <a-button
            type="primary"
            :loading="modalLoading"
            @click="handleSaveProduct"
            class="yellow-button"
          >저장</a-button>
        </div>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { PlusOutlined } from '@ant-design/icons-vue';
import productMasterApis from './api';
import { twoBtnDialog } from '@/utils/utils';
import { BillingTypes, getUsageFeeLabel, getBillingTypeLabel } from '../enums';

export default {
  name: 'ProductMasterList',
  components: {
    PlusOutlined,
  },
  data() {
    return {
      searchKeyword: '',
      includeInactive: false,
      dataLoading: false,
      productList: [],

      // 모달 상태
      modalVisible: false,
      modalLoading: false,
      isEditMode: false,
      selectedProductId: null,

      formData: {
        name: '',
        billingType: 'monthly',
        installationFee: 0,
        price: 0,
        description: '',
      },

      billingTypeOptions: Object.values(BillingTypes),

      formRules: {
        name: [{ required: true, message: '제품명을 입력하세요', trigger: 'blur' }],
        billingType: [{ required: true, message: '과금 방식을 선택하세요', trigger: 'change' }],
        installationFee: [
          { required: true, message: '설치비를 입력하세요', trigger: 'blur' },
          { type: 'number', min: 0, message: '설치비는 0 이상이어야 합니다', trigger: 'blur' },
        ],
        price: [
          { required: true, message: '사용료를 입력하세요', trigger: 'blur' },
          { type: 'number', min: 0, message: '사용료는 0 이상이어야 합니다', trigger: 'blur' },
        ],
      },

      columns: [
        {
          title: '제품명',
          dataIndex: 'name',
          key: 'name',
          width: 150,
        },
        {
          title: '과금 방식',
          dataIndex: 'billingType',
          key: 'billingType',
          width: 100,
          align: 'center',
          customRender: ({ text }) => getBillingTypeLabel(text),
        },
        {
          title: '설치비',
          dataIndex: 'installationFee',
          key: 'installationFee',
          width: 120,
          align: 'right',
          customRender: ({ text }) => `${(text || 0).toLocaleString()}원`,
        },
        {
          title: '사용료',
          dataIndex: 'price',
          key: 'price',
          width: 120,
          align: 'right',
          customRender: ({ text }) => `${(text || 0).toLocaleString()}원`,
        },
        {
          title: '설명',
          dataIndex: 'description',
          key: 'description',
          ellipsis: true,
        },
        {
          title: '상태',
          dataIndex: 'isActive',
          key: 'isActive',
          width: 80,
          align: 'center',
        },
        {
          title: '작업',
          key: 'actions',
          width: 150,
          align: 'center',
        },
      ],
    };
  },
  computed: {
    usageFeeLabel() {
      return getUsageFeeLabel(this.formData.billingType);
    },
  },
  mounted() {
    this.loadProductMasters();
  },
  methods: {
    // API 핸들러
    apiHandler() {
      return productMasterApis(this.$apiInstance, this.$store);
    },

    // 제품 목록 조회
    loadProductMasters() {
      this.dataLoading = true;

      this.apiHandler().getProductMasters(
        { includeInactive: this.includeInactive },
        (response) => {
          if (response.status === 200 || response.status === 201) {
            let data = response.data || [];
            // 검색어 필터링 (클라이언트 측)
            if (this.searchKeyword) {
              data = data.filter((item) =>
                item.name.toLowerCase().includes(this.searchKeyword.toLowerCase())
              );
            }
            this.productList = data;
          }
          this.dataLoading = false;
        },
        (error) => {
          console.error('제품 목록 조회 실패:', error);
          this.$message.error('제품 목록을 불러오는데 실패했습니다.');
          this.productList = [];
          this.dataLoading = false;
        }
      );
    },

    // 숫자만 입력 허용
    onlyNumber(event) {
      const charCode = event.which ? event.which : event.keyCode;
      if (charCode < 48 || charCode > 57) {
        event.preventDefault();
      }
    },

    // 제품 추가 모달 열기
    handleAddProduct() {
      this.isEditMode = false;
      this.selectedProductId = null;
      this.formData = {
        name: '',
        billingType: 'monthly',
        installationFee: 0,
        price: 0,
        description: '',
      };
      this.modalVisible = true;
    },

    // 제품 수정 모달 열기
    handleEditProduct(record) {
      this.isEditMode = true;
      this.selectedProductId = record._id;
      this.formData = {
        name: record.name,
        billingType: record.billingType || 'monthly',
        installationFee: record.installationFee || 0,
        price: record.price || 0,
        description: record.description || '',
      };
      this.modalVisible = true;
    },

    // 제품 삭제
    async handleDeleteProduct(record) {
      const isConfirmed = await twoBtnDialog(
        '삭제 확인',
        `"${record.name}" 제품을 삭제하시겠습니까?`,
        '삭제'
      );

      if (!isConfirmed) return;

      this.apiHandler().deleteProductMaster(
        record._id,
        () => {
          this.$message.success('제품이 삭제되었습니다.');
          this.loadProductMasters();
        },
        (error) => {
          console.error('제품 삭제 실패:', error);
          this.$message.error('제품 삭제에 실패했습니다.');
        }
      );
    },

    // 제품 저장 (추가/수정)
    async handleSaveProduct() {
      try {
        await this.$refs.formRef.validate();
      } catch {
        return;
      }

      this.modalLoading = true;

      const saveData = {
        name: this.formData.name,
        billingType: this.formData.billingType,
        installationFee: Number(this.formData.installationFee || 0),
        price: Number(this.formData.price || 0),
        description: this.formData.description,
      };

      if (this.isEditMode && this.selectedProductId) {
        this.apiHandler().updateProductMaster(
          this.selectedProductId,
          saveData,
          () => {
            this.$message.success('제품이 수정되었습니다.');
            this.modalVisible = false;
            this.loadProductMasters();
            this.modalLoading = false;
          },
          (error) => {
            console.error('제품 수정 실패:', error);
            this.$message.error(error.response?.data?.message || '제품 수정에 실패했습니다.');
            this.modalLoading = false;
          }
        );
      } else {
        this.apiHandler().createProductMaster(
          saveData,
          () => {
            this.$message.success('제품이 추가되었습니다.');
            this.modalVisible = false;
            this.loadProductMasters();
            this.modalLoading = false;
          },
          (error) => {
            console.error('제품 추가 실패:', error);
            this.$message.error(error.response?.data?.message || '제품 추가에 실패했습니다.');
            this.modalLoading = false;
          }
        );
      }
    },

    // 모달 취소
    handleCancel() {
      this.modalVisible = false;
    },
  },
};
</script>

<style scoped lang="scss">
.titleContainer {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.page-title {
  color: #000;
  font-size: 1.8rem;
}

.pageDesc {
  color: #6c757d;
  font-size: 14px;
  margin-top: 8px;
}

.table-buttons {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin: 15px;
}

.add-product-btn {
  margin-left: 16px;
}

.table-wrap {
  margin: 16px;
}

.noData {
  text-align: center;
  padding: 24px;
  color: #999;
}

.product-name {
  font-weight: 500;
  color: #1a1a2e;
}

:deep(.yellow-button) {
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;

  &:hover {
    background-color: #E6B82E !important;
    border-color: #E6B82E !important;
  }

  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}

@media screen and (max-width: 767px) {
  .card-header {
    margin: 0;
  }
}

/* 모달 스타일 */
.modal-form {
  padding: 20px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
}
</style>
